/**
 * Polyfills for built-ins missing on old browsers/WebViews — e.g. the Android System WebView
 * 91 that still shows up in Sentry (THE-NEW-Y6) on devices that never updated it.
 *
 * The build's syntax downlevel converts syntax but does NOT inject API polyfills, so the
 * bundle loads fine there and only throws when one of these built-ins is called:
 *
 * - Object.fromEntries (Chrome 73) — used by @angular/animations when building keyframes.
 * - Promise.allSettled (Chrome 76)
 * - structuredClone    (Chrome 98) — used by @account/ui, settings, dx-data-grid, filegroup.
 *
 * Each one is installed only when missing; modern browsers keep the native implementation.
 * This file must be loaded BEFORE zone.js in the polyfills array.
 */

type ObjectFromEntries = (entries: Iterable<readonly [PropertyKey, unknown]>) => Record<PropertyKey, unknown>;
type PromiseAllSettled = (values: Iterable<unknown>) => Promise<unknown[]>;

const globalScope = globalThis as { structuredClone?: <T>(value: T) => T };
const objectCtor = Object as { fromEntries?: ObjectFromEntries };
const promiseCtor = Promise as unknown as { allSettled?: PromiseAllSettled };

/***************************************************************************************************
 * Object.fromEntries (ES2019)
 */
if (typeof objectCtor.fromEntries !== 'function') {
  objectCtor.fromEntries = (entries) => {
    if (entries === null || entries === undefined) {
      throw new TypeError('Object.fromEntries requires an iterable argument');
    }
    const result: Record<PropertyKey, unknown> = {};
    for (const entry of entries) {
      result[entry[0]] = entry[1];
    }
    return result;
  };
}

/***************************************************************************************************
 * Promise.allSettled (ES2020)
 */
if (typeof promiseCtor.allSettled !== 'function') {
  promiseCtor.allSettled = (values) =>
    Promise.all(
      Array.from(values, (value) =>
        Promise.resolve(value).then(
          (v) => ({ status: 'fulfilled', value: v }),
          (reason) => ({ status: 'rejected', reason }),
        ),
      ),
    );
}

/***************************************************************************************************
 * structuredClone (Chrome 98)
 *
 * Covers what the apps actually clone: plain objects/arrays of state, Date, Map, Set and
 * circular references. Cases the apps never clone (Blob, File, ArrayBuffer, RegExp, Error,
 * transferables) are out of scope.
 */
function deepClone<T>(value: T, seen: WeakMap<object, unknown>): T {
  if (value === null || typeof value !== 'object') return value;

  const source = value as unknown as object;
  if (seen.has(source)) return seen.get(source) as T;

  // The one place `new Date` is right: a millis-to-millis copy, before Luxon exists (lint-exempt).
  if (value instanceof Date) return new Date(value.getTime()) as unknown as T;

  if (Array.isArray(value)) {
    const copy: unknown[] = [];
    seen.set(source, copy);
    for (const item of value) copy.push(deepClone(item, seen));
    return copy as unknown as T;
  }

  if (value instanceof Map) {
    const copy = new Map();
    seen.set(source, copy);
    value.forEach((v, k) => copy.set(deepClone(k, seen), deepClone(v, seen)));
    return copy as unknown as T;
  }

  if (value instanceof Set) {
    const copy = new Set();
    seen.set(source, copy);
    value.forEach((v) => copy.add(deepClone(v, seen)));
    return copy as unknown as T;
  }

  const copy: Record<string, unknown> = {};
  seen.set(source, copy);
  for (const key of Object.keys(value as Record<string, unknown>)) {
    copy[key] = deepClone((value as Record<string, unknown>)[key], seen);
  }
  return copy as unknown as T;
}

if (typeof globalScope.structuredClone !== 'function') {
  globalScope.structuredClone = <T>(value: T): T => deepClone(value, new WeakMap());
}

export {};
